import { useCallback } from 'react';
import { parse, isValid } from 'date-fns';
import { ESBA_DATE_FORMAT } from '../index';

type UseDateInputFinishOptions = {
    onFinish?: (date: Date | null) => void;
    dateFormat?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

/**
 * Custom hook for handling Enter key press and blur events in date input fields.
 * Parses the input value and calls onFinish callback with valid date or null for empty input.
 * 
 * @param options - Configuration options
 * @param options.onFinish - Callback to execute when Enter is pressed or input loses focus. Called with Date for valid input, null for empty input
 * @param options.dateFormat - Date format for parsing (default: ESBA_DATE_FORMAT)
 * @param options.onKeyDown - Optional additional keydown handler
 * @param options.onBlur - Optional additional blur handler
 * @returns Object with handleKeyDown and handleBlur functions
 */
export const useDateInputFinish = ({
    onFinish,
    dateFormat = ESBA_DATE_FORMAT,
    onKeyDown,
    onBlur,
}: UseDateInputFinishOptions = {}) => {
    const validateAndFinish = useCallback(
        (inputValue: string) => {
            if (!onFinish) return;

            if (!inputValue || inputValue.trim() === '') {
                onFinish(null);
                return;
            }
            const parsed = parse(inputValue, dateFormat, new Date());
            if (isValid(parsed)) {
                onFinish(parsed);
            }
        },
        [onFinish, dateFormat]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Call any existing onKeyDown handler first
            if (onKeyDown) {
                onKeyDown(e);
            }

            // Handle Enter key for date selection
            if (e.key === 'Enter' && onFinish) {
                const inputValue = e.currentTarget.value;
                validateAndFinish(inputValue);
                e.currentTarget.blur();
            }
        },
        [onFinish, onKeyDown, validateAndFinish]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Call any existing onBlur handler first
            if (onBlur) {
                onBlur(e);
            }

            // Skip if focus moved to the datepicker calendar popup
            const relatedTarget = e.relatedTarget as HTMLElement | null;
            if (relatedTarget?.closest('.react-datepicker')) {
                return;
            }

            // Handle blur for date selection
            if (onFinish) {
                const inputValue = e.currentTarget.value;
                validateAndFinish(inputValue);
            }
        },
        [onFinish, onBlur, validateAndFinish]
    );

    return { handleKeyDown, handleBlur };
};
